// @ts-check
/** @typedef {import('../types/api').Announcement} Announcement */

import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Save, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import announcementsService from '../services/announcementsService';

const AnnouncementsManager = () => {
  const toast = useToast();
  /** @type {[Announcement[], Function]} */
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const data = await announcementsService.getAll();
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load announcements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const handleAdd = async () => {
    try {
      const created = await announcementsService.create({
        text: 'Enjoy complimentary shipping on all your orders',
        link: '/ready-to-wear',
      });
      setAnnouncements((prev) => [...prev, created]);
      toast.success('Announcement added');
    } catch (err) {
      toast.error('Failed to add announcement');
      console.error(err);
    }
  };

  const handleReorder = async (id, direction) => {
    const idx = announcements.findIndex((a) => a.id === id);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= announcements.length) return;

    const updated = [...announcements];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((a, i) => (a.display_order = i));
    setAnnouncements(updated);

    try {
      await announcementsService.reorder(updated.map((a) => ({ id: a.id, display_order: a.display_order })));
    } catch (err) {
      toast.error('Failed to reorder announcements');
      console.error(err);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await announcementsService.update(announcement.id, { is_active: !announcement.is_active });
      setAnnouncements((prev) => prev.map((a) => (a.id === announcement.id ? { ...a, is_active: !a.is_active } : a)));
    } catch (err) {
      toast.error('Failed to toggle announcement');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await announcementsService.delete(id);
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
      toast.success('Announcement deleted');
    } catch (err) {
      toast.error('Failed to delete announcement');
      console.error(err);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setAnnouncements((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
  };

  const handleSave = async (announcement) => {
    setSaving(true);
    try {
      await announcementsService.update(announcement.id, {
        text: announcement.text,
        link: announcement.link,
      });
      toast.success('Announcement updated');
    } catch (err) {
      toast.error('Failed to save announcement');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading announcements...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition"
        >
          <Plus size={18} />
          Add Announcement
        </button>
      </div>

      {announcements.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No announcements configured. Add one to show the announcement bar.
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((announcement, index) => (
            <div
              key={announcement.id}
              className={`flex items-start gap-4 p-4 border border-border rounded-lg transition ${
                announcement.is_active ? 'bg-card' : 'bg-muted opacity-60'
              }`}
            >
              <div className="flex-1 space-y-2">
                <span className="text-xs text-muted-foreground/70">#{index + 1}</span>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={announcement.text || ''}
                    onChange={(e) => handleFieldChange(announcement.id, 'text', e.target.value)}
                    placeholder="Announcement text"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                  <input
                    type="text"
                    value={announcement.link || ''}
                    onChange={(e) => handleFieldChange(announcement.id, 'link', e.target.value)}
                    placeholder="Link (optional, e.g. /ready-to-wear)"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>

                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Use <code className="bg-blue-50 dark:bg-blue-950/50 px-1 rounded-md">{'{threshold}'}</code> to show
                  the free-shipping threshold in the visitor's currency (e.g. ₹40,000 / $500 / £400)
                </p>

                <button
                  onClick={() => handleSave(announcement)}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition mt-1"
                >
                  <Save size={14} />
                  Save
                </button>
              </div>

              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={() => handleReorder(announcement.id, 'up')}
                  disabled={index === 0}
                  className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                  title="Move up"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={() => handleReorder(announcement.id, 'down')}
                  disabled={index === announcements.length - 1}
                  className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                  title="Move down"
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  onClick={() => handleToggleActive(announcement)}
                  className="p-1.5 rounded-lg hover:bg-accent transition"
                  title={announcement.is_active ? 'Deactivate' : 'Activate'}
                >
                  {announcement.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => handleDelete(announcement.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsManager;
